import React, { useState, useEffect, useMemo } from 'react';
import { adminService } from '../../services/adminService';
import './Reports.css';

interface RevenueData {
  date: string;
  revenue: number;
  bookings: number;
  participants: number;
}

interface TourPerformance {
  tourType: string;
  bookingCount: number;
  totalRevenue: number;
  averageParticipants: number;
  popularityScore: number;
}

interface CustomerAnalytics {
  totalCustomers: number;
  newCustomers: number;
  returningCustomers: number;
  vipCustomers: number;
  averageBookingValue: number;
  topCustomers: Array<{
    id: string;
    name: string;
    bookings: number;
    totalSpent: number;
  }>;
}

interface SeasonalData {
  month: string;
  revenue: number;
  bookings: number;
  occupancyRate: number;
}

interface ReportFilters {
  dateRange: { start: string; end: string };
  reportType: 'revenue' | 'occupancy' | 'customers' | 'tours' | 'seasonal';
  granularity: 'daily' | 'weekly' | 'monthly' | 'yearly';
}
const Reports: React.FC = () => {
  const [filters, setFilters] = useState<ReportFilters>({
    dateRange: {
      start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
      end: new Date().toISOString().split('T')[0]
    },
    reportType: 'revenue',
    granularity: 'daily'
  });

  const [revenueData, setRevenueData] = useState<RevenueData[]>([]);
  const [tourPerformance, setTourPerformance] = useState<TourPerformance[]>([]);
  const [customerAnalytics, setCustomerAnalytics] = useState<CustomerAnalytics>({
    totalCustomers: 0,
    newCustomers: 0,
    returningCustomers: 0,
    vipCustomers: 0,
    averageBookingValue: 0,
    topCustomers: []
  });
  const [seasonalData, setSeasonalData] = useState<SeasonalData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Mock-Daten generieren
  const generateMockRevenueData = (): RevenueData[] => {
    const data: RevenueData[] = [];
    const start = new Date(filters.dateRange.start);
    const end = new Date(filters.dateRange.end);
    const current = new Date(start);
    
    while (current <= end) {
      data.push({
        date: current.toISOString().split('T')[0],
        revenue: Math.floor(Math.random() * 5000) + 1000,
        bookings: Math.floor(Math.random() * 10) + 1,
        participants: Math.floor(Math.random() * 100) + 20
      });
      current.setDate(current.getDate() + 1);
    }
    return data;
  };
  const mockTourPerformance: TourPerformance[] = [
    {
      tourType: 'Floßfahrt Premium',
      bookingCount: 145,
      totalRevenue: 72500,
      averageParticipants: 32,
      popularityScore: 95
    },
    {
      tourType: 'Floßfahrt Standard',
      bookingCount: 289,
      totalRevenue: 86700,
      averageParticipants: 24,
      popularityScore: 88
    },
    {
      tourType: 'Floßfahrt ALL IN',
      bookingCount: 76,
      totalRevenue: 45600,
      averageParticipants: 45,
      popularityScore: 72
    },
    {
      tourType: 'Betriebsausflug Special',
      bookingCount: 42,
      totalRevenue: 58800,
      averageParticipants: 68,
      popularityScore: 68
    },
    {
      tourType: 'Hochzeitspaket',
      bookingCount: 18,
      totalRevenue: 36000,
      averageParticipants: 55,
      popularityScore: 85
    }
  ];
  const mockCustomerAnalytics: CustomerAnalytics = {
    totalCustomers: 1247,
    newCustomers: 89,
    returningCustomers: 256,
    vipCustomers: 34,
    averageBookingValue: 1458.50,
    topCustomers: [
      { id: '1', name: 'Siemens AG', bookings: 12, totalSpent: 28500 },
      { id: '2', name: 'BMW Group', bookings: 8, totalSpent: 19200 },
      { id: '3', name: 'Adidas AG', bookings: 6, totalSpent: 15600 },
      { id: '4', name: 'Schaeffler Gruppe', bookings: 5, totalSpent: 12500 },
      { id: '5', name: 'Continental AG', bookings: 5, totalSpent: 11000 }
    ]
  };

  const mockSeasonalData: SeasonalData[] = [
    { month: 'Januar', revenue: 12500, bookings: 15, occupancyRate: 35 },
    { month: 'Februar', revenue: 18900, bookings: 22, occupancyRate: 42 },
    { month: 'März', revenue: 28500, bookings: 35, occupancyRate: 58 },
    { month: 'April', revenue: 42300, bookings: 52, occupancyRate: 72 },
    { month: 'Mai', revenue: 68900, bookings: 85, occupancyRate: 88 },
    { month: 'Juni', revenue: 78500, bookings: 98, occupancyRate: 92 },
    { month: 'Juli', revenue: 82300, bookings: 102, occupancyRate: 95 },
    { month: 'August', revenue: 85600, bookings: 108, occupancyRate: 96 },
    { month: 'September', revenue: 72400, bookings: 89, occupancyRate: 85 },
    { month: 'Oktober', revenue: 45600, bookings: 56, occupancyRate: 68 },
    { month: 'November', revenue: 22300, bookings: 28, occupancyRate: 45 },
    { month: 'Dezember', revenue: 15800, bookings: 18, occupancyRate: 32 }
  ];
  useEffect(() => {
    loadReportData();
  }, [filters]);

  const loadReportData = async () => {
    setIsLoading(true);

    try {
      switch (filters.reportType) {
        case 'revenue':
        case 'occupancy': {
          const response: any = await adminService.getReport('revenue', {
            dateFrom: filters.dateRange.start,
            dateTo: filters.dateRange.end,
            groupBy: filters.granularity
          });

          // Backend returns data in "Data" property (PascalCase)
          const dataArray = response.Data || response.data || [];
          const transformedData = dataArray.map((item: any) => ({
            date: item.Date || item.date,
            revenue: item.Revenue || item.revenue || 0,
            bookings: item.Bookings || item.bookings || 0,
            participants: item.Participants || item.participants || 0
          }));

          setRevenueData(transformedData);
          break;
        }

        case 'tours': {
          const response: any = await adminService.getReport('tour-performance', {
            dateFrom: filters.dateRange.start,
            dateTo: filters.dateRange.end
          });

          // Transform backend response to frontend format
          const transformedTours = (response.TourTypes || response.tourTypes || []).map((tour: any) => ({
            tourType: tour.TourType || tour.tourType,
            bookingCount: tour.BookingCount || tour.bookingCount,
            totalRevenue: tour.TotalRevenue || tour.totalRevenue,
            averageParticipants: tour.AverageParticipants || tour.averageParticipants,
            popularityScore: tour.PopularityScore || tour.popularityScore
          }));

          setTourPerformance(transformedTours);
          break;
        }

        case 'customers': {
          const response: any = await adminService.getReport('customer-analytics', {
            dateFrom: filters.dateRange.start,
            dateTo: filters.dateRange.end
          });

          // Transform backend response to frontend format
          const transformedAnalytics: CustomerAnalytics = {
            totalCustomers: response.TotalCustomers || response.totalCustomers || 0,
            newCustomers: response.NewCustomers || response.newCustomers || 0,
            returningCustomers: response.ReturningCustomers || response.returningCustomers || 0,
            vipCustomers: response.VipCustomers || response.vipCustomers || 0,
            averageBookingValue: response.AverageBookingValue || response.averageBookingValue || 0,
            topCustomers: (response.TopCustomers || response.topCustomers || []).map((c: any) => ({
              id: c.Id || c.id,
              name: c.Name || c.name,
              bookings: c.Bookings || c.bookings,
              totalSpent: c.TotalSpent || c.totalSpent
            }))
          };

          setCustomerAnalytics(transformedAnalytics);
          break;
        }

        case 'seasonal': {
          const currentYear = new Date().getFullYear();
          const response: any = await adminService.getReport('seasonal', {
            dateFrom: `${currentYear}-01-01`,
            dateTo: `${currentYear}-12-31`
          });

          // Transform backend response to frontend format
          const transformedSeasonal = (response.MonthlyData || response.monthlyData || []).map((month: any) => ({
            month: month.Month || month.month,
            revenue: month.Revenue || month.revenue || 0,
            bookings: month.Bookings || month.bookings || 0,
            occupancyRate: month.OccupancyRate || month.occupancyRate || 0
          }));

          setSeasonalData(transformedSeasonal);
          break;
        }
      }
    } catch (error) {
      console.error('Error loading report data:', error);
      // Fallback to mock data on error
      switch (filters.reportType) {
        case 'revenue':
          setRevenueData(generateMockRevenueData());
          break;
        case 'tours':
          setTourPerformance(mockTourPerformance);
          break;
        case 'customers':
          setCustomerAnalytics(mockCustomerAnalytics);
          break;
        case 'seasonal':
          setSeasonalData(mockSeasonalData);
          break;
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Berechnete Statistiken
  const totalStats = useMemo(() => {
    const totalRevenue = revenueData.reduce((sum, d) => sum + d.revenue, 0);
    const totalBookings = revenueData.reduce((sum, d) => sum + d.bookings, 0);
    const totalParticipants = revenueData.reduce((sum, d) => sum + d.participants, 0);
    const averageBookingValue = totalBookings > 0 ? totalRevenue / totalBookings : 0;
    
    return {
      totalRevenue,
      totalBookings,
      totalParticipants,
      averageBookingValue,
      averageParticipantsPerBooking: totalBookings > 0 ? totalParticipants / totalBookings : 0
    };
  }, [revenueData]);
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleExportCSV = () => {
    let csvContent = '';
    let filename = '';
    
    switch (filters.reportType) {
      case 'revenue':
        csvContent = 'Datum;Umsatz;Buchungen;Teilnehmer\n';
        csvContent += revenueData.map(d => 
          `${formatDate(d.date)};${d.revenue};${d.bookings};${d.participants}`
        ).join('\n');
        filename = 'umsatzbericht';
        break;
        
      case 'tours':
        csvContent = 'Tour-Typ;Buchungen;Umsatz;Durchschn. Teilnehmer;Beliebtheit\n';
        csvContent += tourPerformance.map(t => 
          `${t.tourType};${t.bookingCount};${t.totalRevenue};${t.averageParticipants};${t.popularityScore}%`
        ).join('\n');
        filename = 'tour-performance';
        break;
        
      case 'customers':
        csvContent = 'Kunde;Buchungen;Ausgaben\n';
        csvContent += customerAnalytics.topCustomers.map(c => 
          `${c.name};${c.bookings};${c.totalSpent}`
        ).join('\n');
        filename = 'kundenanalyse';
        break;        
      case 'seasonal':
        csvContent = 'Monat;Umsatz;Buchungen;Auslastung\n';
        csvContent += seasonalData.map(s => 
          `${s.month};${s.revenue};${s.bookings};${s.occupancyRate}%`
        ).join('\n');
        filename = 'saisonbericht';
        break;
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleExportPDF = () => {
    // Placeholder für PDF-Export
    alert('PDF-Export wird in der nächsten Version verfügbar sein');
  };

  const getChartData = () => {
    switch (filters.reportType) {
      case 'revenue':
        return revenueData.map(d => ({
          name: formatDate(d.date).substring(0, 5),
          value: d.revenue
        }));
      case 'occupancy':
        return revenueData.map(d => ({
          name: formatDate(d.date).substring(0, 5),
          value: Math.round((d.participants / 140) * 100)
        }));
      case 'seasonal':
        return seasonalData.map(s => ({
          name: s.month.substring(0, 3),
          value: s.revenue
        }));
      default:
        return [];
    }
  };
  return (
    <div className="reports-container">
      {/* Header */}
      <div className="reports-header">
        <h2>Berichte & Analysen</h2>
        <p>Detaillierte Einblicke in Ihre Geschäftsentwicklung</p>
      </div>

      {/* Filter Controls */}
      <div className="reports-controls">
        <div className="filter-group">
          <label>Berichtstyp</label>
          <select
            value={filters.reportType}
            onChange={(e) => setFilters({ ...filters, reportType: e.target.value as any })}
            className="filter-select"
          >
            <option value="revenue">Umsatzbericht</option>
            <option value="occupancy">Auslastung</option>
            <option value="customers">Kundenanalyse</option>
            <option value="tours">Tour-Performance</option>
            <option value="seasonal">Saisonanalyse</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Zeitraum</label>
          <div className="date-range">
            <input
              type="date"
              value={filters.dateRange.start}
              onChange={(e) => setFilters({
                ...filters,
                dateRange: { ...filters.dateRange, start: e.target.value }
              })}
              className="date-input"
            />
            <span className="date-separator">bis</span>
            <input
              type="date"
              value={filters.dateRange.end}
              onChange={(e) => setFilters({
                ...filters,
                dateRange: { ...filters.dateRange, end: e.target.value }
              })}
              className="date-input"
            />
          </div>
        </div>
        <div className="filter-group">
          <label>Granularität</label>
          <select
            value={filters.granularity}
            onChange={(e) => setFilters({ ...filters, granularity: e.target.value as any })}
            className="filter-select"
          >
            <option value="daily">Täglich</option>
            <option value="weekly">Wöchentlich</option>
            <option value="monthly">Monatlich</option>
            <option value="yearly">Jährlich</option>
          </select>
        </div>

        <div className="export-buttons">
          <button onClick={handleExportCSV} className="btn btn-secondary">
            📥 CSV Export
          </button>
          <button onClick={handleExportPDF} className="btn btn-secondary">
            📄 PDF Export
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      )}

      {/* Summary Stats */}
      {!isLoading && filters.reportType === 'revenue' && (
        <div className="summary-stats">
          <div className="stat-card primary">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <div className="stat-label">Gesamtumsatz</div>
              <div className="stat-value">{formatCurrency(totalStats.totalRevenue)}</div>
            </div>
          </div>
          <div className="stat-card success">
            <div className="stat-icon">📅</div>
            <div className="stat-content">
              <div className="stat-label">Buchungen</div>
              <div className="stat-value">{totalStats.totalBookings}</div>
            </div>
          </div>          <div className="stat-card info">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <div className="stat-label">Teilnehmer</div>
              <div className="stat-value">{totalStats.totalParticipants}</div>
            </div>
          </div>
          <div className="stat-card warning">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <div className="stat-label">Ø Buchungswert</div>
              <div className="stat-value">{formatCurrency(totalStats.averageBookingValue)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="report-content">
        {/* Revenue Report */}
        {!isLoading && filters.reportType === 'revenue' && (
          <div className="report-section">
            <h3>Umsatzentwicklung</h3>
            <div className="chart-container">
              {/* Placeholder für Chart */}
              <div className="chart-placeholder">
                <p>📊 Chart-Visualisierung</p>
                <p>Integration mit Recharts oder Chart.js erforderlich</p>
              </div>
            </div>
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Umsatz</th>
                    <th>Buchungen</th>
                    <th>Teilnehmer</th>
                    <th>Ø pro Buchung</th>
                  </tr>
                </thead>
                <tbody>
                  {revenueData.slice(0, 10).map((data, index) => (
                    <tr key={index}>
                      <td>{formatDate(data.date)}</td>
                      <td>{formatCurrency(data.revenue)}</td>
                      <td>{data.bookings}</td>
                      <td>{data.participants}</td>
                      <td>{formatCurrency(data.revenue / data.bookings)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* Tour Performance Report */}
        {!isLoading && filters.reportType === 'tours' && (
          <div className="report-section">
            <h3>Tour-Performance Analyse</h3>
            <div className="performance-grid">
              {tourPerformance.map((tour, index) => (
                <div key={index} className="performance-card">
                  <div className="performance-header">
                    <h4>{tour.tourType}</h4>
                    <span className="popularity-badge" style={{
                      background: `linear-gradient(90deg, #4CAF50 ${tour.popularityScore}%, #e0e0e0 ${tour.popularityScore}%)`
                    }}>
                      {tour.popularityScore}% Beliebtheit
                    </span>
                  </div>
                  <div className="performance-stats">
                    <div className="perf-stat">
                      <span className="perf-label">Buchungen:</span>
                      <span className="perf-value">{tour.bookingCount}</span>
                    </div>
                    <div className="perf-stat">
                      <span className="perf-label">Umsatz:</span>
                      <span className="perf-value">{formatCurrency(tour.totalRevenue)}</span>
                    </div>
                    <div className="perf-stat">
                      <span className="perf-label">Ø Teilnehmer:</span>
                      <span className="perf-value">{tour.averageParticipants}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Customer Analytics Report */}
        {!isLoading && filters.reportType === 'customers' && (
          <div className="report-section">
            <h3>Kundenanalyse</h3>
            <div className="customer-stats">
              <div className="stat-box">
                <div className="stat-number">{customerAnalytics.totalCustomers}</div>
                <div className="stat-label">Gesamtkunden</div>
              </div>
              <div className="stat-box highlight-new">
                <div className="stat-number">+{customerAnalytics.newCustomers}</div>
                <div className="stat-label">Neukunden</div>
              </div>
              <div className="stat-box highlight-returning">
                <div className="stat-number">{customerAnalytics.returningCustomers}</div>
                <div className="stat-label">Stammkunden</div>
              </div>
              <div className="stat-box highlight-vip">
                <div className="stat-number">{customerAnalytics.vipCustomers}</div>
                <div className="stat-label">VIP-Kunden</div>
              </div>
              <div className="stat-box">
                <div className="stat-number">{formatCurrency(customerAnalytics.averageBookingValue)}</div>
                <div className="stat-label">Ø Buchungswert</div>
              </div>
            </div>
            
            <div className="top-customers">
              <h4>Top-Kunden</h4>
              <div className="customer-ranking">
                {customerAnalytics.topCustomers.map((customer, index) => (
                  <div key={customer.id} className="customer-rank-item">
                    <div className="rank-number">#{index + 1}</div>
                    <div className="customer-info">
                      <div className="customer-name">{customer.name}</div>
                      <div className="customer-stats">
                        {customer.bookings} Buchungen • {formatCurrency(customer.totalSpent)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {/* Seasonal Analysis Report */}
        {!isLoading && filters.reportType === 'seasonal' && (
          <div className="report-section">
            <h3>Saisonale Analyse</h3>
            <div className="seasonal-overview">
              <div className="chart-container">
                {/* Placeholder für Saison-Chart */}
                <div className="chart-placeholder">
                  <p>📈 Jahresübersicht</p>
                  <p>Umsatz- und Auslastungsentwicklung</p>
                </div>
              </div>
              <div className="seasonal-table">
                <table>
                  <thead>
                    <tr>
                      <th>Monat</th>
                      <th>Umsatz</th>
                      <th>Buchungen</th>
                      <th>Auslastung</th>
                      <th>Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seasonalData.map((month, index) => (
                      <tr key={index}>
                        <td>{month.month}</td>
                        <td>{formatCurrency(month.revenue)}</td>
                        <td>{month.bookings}</td>
                        <td>
                          <div className="occupancy-bar">
                            <div 
                              className="occupancy-fill"
                              style={{ 
                                width: `${month.occupancyRate}%`,
                                backgroundColor: month.occupancyRate > 80 ? '#4CAF50' : 
                                               month.occupancyRate > 50 ? '#FF9800' : '#F44336'
                              }}
                            >
                              {month.occupancyRate}%
                            </div>
                          </div>
                        </td>
                        <td>
                          {index > 0 && (
                            <span className={month.revenue > seasonalData[index - 1].revenue ? 'trend-up' : 'trend-down'}>
                              {month.revenue > seasonalData[index - 1].revenue ? '📈' : '📉'}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Occupancy Report */}
        {!isLoading && filters.reportType === 'occupancy' && (
          <div className="report-section">
            <h3>Auslastungsbericht</h3>
            <div className="occupancy-content">
              <div className="chart-container">
                <div className="chart-placeholder">
                  <p>📊 Auslastungsverlauf</p>
                  <p>Kapazitätsnutzung über Zeit</p>
                </div>
              </div>
              <div className="occupancy-summary">
                <div className="summary-card">
                  <h4>Durchschnittliche Auslastung</h4>
                  <div className="big-number">68.5%</div>
                  <p>Im gewählten Zeitraum</p>
                </div>
                <div className="summary-card">
                  <h4>Spitzenauslastung</h4>
                  <div className="big-number">96%</div>
                  <p>August 2025</p>
                </div>
                <div className="summary-card">
                  <h4>Niedrigste Auslastung</h4>
                  <div className="big-number">32%</div>
                  <p>Dezember 2025</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;